import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Globe, FilePlus, ArrowRight } from 'lucide-react';

interface CreateSitemapDialogProps {
  show: boolean;
  onClose: () => void;
  onCreateScratch: () => void;
}

export const CreateSitemapDialog: React.FC<CreateSitemapDialogProps> = ({
  show,
  onClose,
  onCreateScratch,
}) => {
  const [mode, setMode] = useState<'select' | 'fetch'>('select');
  const [url, setUrl] = useState('');

  // Reset state when closing
  const handleClose = () => {
    setMode('select');
    setUrl('');
    onClose();
  };

  const handleFetch = (e: React.FormEvent) => {
    e.preventDefault();
    // For now, fetching from URL is not fully implemented on the backend.
    // We can just show an alert or fall back to scratch for now.
    alert('Fetch from URL is coming soon! For now, starting from scratch.');
    onCreateScratch();
    handleClose();
  };

  return (
    <AnimatePresence>
      {show && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            className="relative w-full max-w-lg bg-card border border-border shadow-soft-xl rounded-2xl overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/30">
              <h3 className="text-lg font-bold text-foreground">
                {mode === 'select' ? 'Create New Sitemap' : 'Fetch from URL'}
              </h3>
              <button
                onClick={handleClose}
                className="text-muted-foreground hover:text-foreground bg-background rounded-full p-1 border border-border shadow-sm transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {mode === 'select' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => {
                      onCreateScratch();
                      handleClose();
                    }}
                    className="flex flex-col items-center justify-center p-8 border-2 border-border border-dashed rounded-xl hover:border-workspace-accent hover:bg-workspace-accent/5 transition-all text-center group"
                  >
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4 group-hover:bg-workspace-accent/10 group-hover:text-workspace-accent transition-colors">
                      <FilePlus className="w-6 h-6 text-muted-foreground group-hover:text-workspace-accent transition-colors" />
                    </div>
                    <span className="font-semibold text-foreground">Start from Scratch</span>
                    <span className="text-xs text-muted-foreground mt-2">Build a sitemap manually with our visual editor.</span>
                  </button>

                  <button
                    onClick={() => setMode('fetch')}
                    className="flex flex-col items-center justify-center p-8 border-2 border-border border-dashed rounded-xl hover:border-workspace-accent hover:bg-workspace-accent/5 transition-all text-center group"
                  >
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4 group-hover:bg-workspace-accent/10 group-hover:text-workspace-accent transition-colors">
                      <Globe className="w-6 h-6 text-muted-foreground group-hover:text-workspace-accent transition-colors" />
                    </div>
                    <span className="font-semibold text-foreground">Fetch from URL</span>
                    <span className="text-xs text-muted-foreground mt-2">Automatically generate a sitemap from a website.</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleFetch} className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Website URL</label>
                    <input
                      type="url"
                      placeholder="https://example.com"
                      className="w-full bg-background border border-input rounded-lg px-4 py-2.5 text-foreground focus:ring-2 focus:ring-workspace-accent focus:border-transparent transition-all outline-none"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      required
                    />
                    <p className="text-xs text-muted-foreground">
                      We'll scan the website and automatically generate a sitemap based on its pages.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setMode('select')}
                      className="flex-1 px-4 py-2.5 bg-muted text-foreground font-medium rounded-lg hover:bg-muted/80 transition-colors"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-1 flex items-center justify-center px-4 py-2.5 bg-workspace-accent hover:bg-workspace-accent-hover text-white font-medium rounded-lg transition-colors"
                    >
                      Fetch Sitemap <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
